/**
 * Browser sleep/wake and network online/offline event handlers
 * for WebSocket reconnection
 */

export class ConnectionEventHandlers {
  private socket: any;
  private wrapper: any;
  private lastPingTime: number = Date.now();
  private visibilityChangeHandler: (() => void) | null = null;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;

  constructor(socket: any, wrapper?: any) {
    this.socket = socket;
    this.wrapper = wrapper;
  }

  /**
   * Initialize all event handlers
   */
  initialize(): void {
    this.setupVisibilityChangeHandler();
    this.setupNetworkHandlers();
  }

  /**
   * Clean up all event handlers
   */
  cleanup(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
    }

    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
    }
  }

  /**
   * Update last ping time (call this when ping is sent)
   */
  updateLastPingTime(): void {
    this.lastPingTime = Date.now();
  }

  /**
   * Set up page visibility change handler (browser sleep/wake)
   */
  private setupVisibilityChangeHandler(): void {
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page became visible - checking connection');

        const timeSinceLastPing = Date.now() - this.lastPingTime;

        // Check socket state first
        if (this.socket && this.socket.connectionManager) {
          const currentProtocol = this.socket.connectionManager.currentProtocol;
          const socket = this.socket.connectionManager.connections.get(currentProtocol);
          const connectionState = this.socket.connectionManager.getConnectionState();

          // If socket is closed or connection is already offline/reconnecting, let existing logic handle it
          if (!socket || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
            console.log('Socket is already closed - skipping visibility reconnect');
            return;
          }

          // If connection is already reconnecting, don't interfere
          if (connectionState === 'reconnecting' || connectionState === 'connecting') {
            console.log('Already reconnecting - skipping visibility reconnect');
            return;
          }

          // If socket is OPEN, just send a ping to verify connection
          // Don't forcefully close it - let the natural ping/pong mechanism handle it
          if (socket.readyState === WebSocket.OPEN) {
            console.log(`Socket is OPEN, time since last ping: ${Math.round(timeSinceLastPing / 1000)}s - sending verification ping`);

            // Send ping to verify connection is still alive
            if (this.socket.authSessionManager) {
              this.socket.authSessionManager.handleSendPing();
            }
            return;
          }
        }
      } else {
        console.log('Page became hidden');
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    console.log('Visibility change handler registered');
  }

  /**
   * Set up network online/offline event handlers
   */
  private setupNetworkHandlers(): void {
    this.onlineHandler = () => {
      console.log('Network online event detected');

      if (this.socket && this.socket.connectionManager) {
        const currentProtocol = this.socket.connectionManager.currentProtocol;
        const socket = this.socket.connectionManager.connections.get(currentProtocol);

        // CRITICAL: Check socket state FIRST, not connectionState
        // If socket is still OPEN (regardless of connectionState), don't reconnect
        if (socket && socket.readyState === WebSocket.OPEN) {
          console.log('Socket is still OPEN - no need to reconnect. Sending verification ping.');

          // Reset connectionState if it was set to offline
          const connectionState = this.socket.connectionManager.getConnectionState();
          console.log(`Network online - socket OPEN, connectionState: ${connectionState}`);

          if (connectionState === 'offline') {
            console.log('Resetting connectionState from offline to authenticated');
            this.socket.connectionManager.connectionState = 'authenticated';
            this.socket.connectionManager.emit('connectionStateChanged', 'authenticated');

            // IMPORTANT: Flush queued messages now that network is back
            if (this.wrapper && this.wrapper.flushMessageQueue) {
              console.log('Network back online - calling flushMessageQueue() on wrapper');
              this.wrapper.flushMessageQueue();
            } else {
              console.error('flushMessageQueue not found on wrapper');
            }
          }

          // Send ping to verify connection
          if (this.socket.authSessionManager) {
            this.socket.authSessionManager.handleSendPing();
          }
          return;
        }

        // Socket is closed - check if we should reconnect
        const connectionState = this.socket.connectionManager.getConnectionState();
        if (connectionState === 'offline' || connectionState === 'reconnecting' || connectionState === 'disconnected') {
          console.log('Socket is CLOSED and network is back - triggering reconnection');

          // Reset retry counter and attempt reconnection
          this.socket.connectionManager.reconnectionManager.reset();
          this.socket.connectionManager.manualReconnect();
        }
      }
    };

    this.offlineHandler = () => {
      console.log('Network offline event detected');

      if (this.socket && this.socket.connectionManager) {
        // Always update state to offline when network goes down
        // This provides immediate UI feedback to the user
        // Socket will close naturally when it detects the network is down
        console.log('Updating connectionState to offline due to network loss');
        this.socket.connectionManager.connectionState = 'offline';
        this.socket.connectionManager.emit('connectionStateChanged', 'offline');
      }
    };

    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);
    console.log('Network event handlers registered');
  }
}
